import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = '/insolvio-logo-cropped.png';

// Swedish cookies content — Version 1.0, gällande från 2026-10-07
const cookiesContentSv = {
  title: 'Cookiepolicy',
  tocTitle: 'Innehåll',
  toc: [
    'Inledning',
    'Vad är cookies?',
    'Hur använder vi cookies?',
    'Typer av cookies vi använder',
    'Tredjepartscookies',
    'Ditt samtycke och hur du hanterar cookies',
    'Rättslig grund',
    'Ändringar av denna cookiepolicy',
    'Kontaktuppgifter',
  ],
  sections: [
    {
      title: '1. Inledning',
      content: `Insolvio ("vi", "oss" eller "vår") använder cookies och liknande spårningstekniker på vår webbplats insolvio.se ("Webbplatsen"). Denna cookiepolicy förklarar vad cookies är, hur vi använder dem, vilka typer av cookies vi använder samt hur du kan hantera dina cookieinställningar.

Nödvändiga cookies används automatiskt eftersom de krävs för att Webbplatsen ska fungera. För eventuella cookies som inte är strikt nödvändiga inhämtar vi ditt samtycke innan sådana cookies placeras, i enlighet med vad som beskrivs i avsnitt 6 nedan. Enbart det faktum att du använder Webbplatsen utgör inte samtycke till icke nödvändiga cookies.`,
    },
    {
      title: '2. Vad är cookies?',
      content: `Cookies är små textfiler som lagras på din enhet (dator, mobiltelefon eller surfplatta) när du besöker en webbplats. Cookies används i stor utsträckning för att webbplatser ska fungera korrekt, effektivare och för att ge webbplatsägare information om hur webbplatsen används.

Cookies kan vara antingen sessionscookies (som raderas när du stänger din webbläsare) eller permanenta cookies (som ligger kvar på din enhet under en bestämd tidsperiod eller tills du tar bort dem manuellt).`,
    },
    {
      title: '3. Hur använder vi cookies?',
      content: `Vi använder för närvarande endast cookies som är strikt nödvändiga för att Webbplatsen ska fungera korrekt och tekniskt. Om vi i framtiden börjar använda cookies för ytterligare ändamål, såsom de som anges nedan, kommer vi att uppdatera denna policy och inhämta ditt samtycke innan sådana cookies placeras:`,
      bulletList: [
        'För att säkerställa att Webbplatsen fungerar korrekt och tekniskt',
        'För att komma ihåg dina preferenser och inställningar (används för närvarande inte – kommer att läggas till om och när sådana cookies börjar användas, med förhandssamtycke där så krävs)',
        'För att analysera hur besökare använder Webbplatsen och förbättra användarupplevelsen (används för närvarande inte)',
        'För att mäta och analysera trafik på Webbplatsen (används för närvarande inte)',
        'För marknadsföringsändamål (används för närvarande inte)',
      ],
    },
    {
      title: '4. Typer av cookies vi använder',
      content: `4.1 Nödvändiga cookies

Dessa cookies är absolut nödvändiga för att Webbplatsen ska fungera korrekt. Utan dessa cookies kan vissa delar av Webbplatsen inte fungera. De kräver inte ditt samtycke och kan inte stängas av. Nedan framgår en sammanställning av samtliga cookies som används på Webbplatsen.`,
      table: {
        headers: ['Namn', 'Leverantör', 'Beskrivning', 'Varaktighet', 'Typ'],
        rows: [
          ['__cf_bm', '.insolvio.se (Cloudflare)', 'Stödjer bothantering och säkerställer att Webbplatsen skyddas mot skadlig trafik.', '1 timme', 'Nödvändig'],
          ['insolvio-language', 'insolvio.se', 'Lagrar användarens valda språkinställning på Webbplatsen.', 'Permanent', 'Nödvändig'],
        ],
      },
    },
    {
      title: '5. Tredjepartscookies',
      content: `Vi tillåter för närvarande inte tredje part att placera icke nödvändiga cookies på din enhet via vår Webbplats. Om detta ändras kommer vi att uppdatera denna policy, lista aktuella tredjepartsleverantörer i tabellen i avsnitt 4 och inhämta ditt förhandssamtycke innan sådana cookies placeras.

Om tredjepartscookies används i framtiden har vi ingen kontroll över dem. Vi rekommenderar att du i så fall läser respektive tredje parts cookiepolicy för mer information.`,
    },
    {
      title: '6. Ditt samtycke och hur du hanterar cookies',
      content: `6.1 Samtycke

När du besöker vår Webbplats för första gången visas en informationsbanner som upplyser dig om att Webbplatsen endast använder strikt nödvändiga cookies som inte kräver ditt samtycke. Om vi i framtiden börjar använda cookies som kräver samtycke kommer bannern att uppdateras så att du kan välja att acceptera eller avvisa respektive kategori, med lika enkla alternativ för båda valen, och så att du när som helst kan återkalla ditt samtycke eller ändra dina inställningar.

6.2 Webbläsarinställningar

Du kan också hantera cookies direkt via din webbläsares inställningar. De flesta webbläsare ger dig möjlighet att:`,
      bulletList: [
        'Se vilka cookies som är lagrade på din enhet',
        'Ta bort cookies, antingen individuellt eller alla på en gång',
        'Blockera cookies från specifika webbplatser',
        'Blockera tredjepartscookies',
        'Blockera alla cookies',
      ],
      footer: `Observera att om du väljer att blockera eller ta bort cookies kan det påverka funktionaliteten på vår Webbplats och din användarupplevelse kan försämras.

Instruktioner för de vanligaste webbläsarna:
• Google Chrome
• Mozilla Firefox
• Safari
• Microsoft Edge`,
    },
    {
      title: '7. Rättslig grund',
      content: `Vi behandlar personuppgifter som samlas in via cookies i enlighet med Europaparlamentets och rådets förordning (EU) 2016/679 (GDPR) samt lagen (2003:389) om elektronisk kommunikation (LEK), som genomför EU:s e-dataskyddsdirektiv (2002/58/EG).

Den rättsliga grunden för:`,
      bulletList: [
        'Nödvändiga cookies: Berättigat intresse (artikel 6.1 f GDPR) och teknisk nödvändighet',
      ],
    },
    {
      title: '8. Ändringar av denna cookiepolicy',
      content: `Vi förbehåller oss rätten att uppdatera denna cookiepolicy när som helst. Eventuella ändringar träder i kraft när den uppdaterade policyn publiceras på Webbplatsen. Vi rekommenderar att du regelbundet granskar denna policy för att hålla dig informerad om hur vi använder cookies.`,
    },
    {
      title: '9. Kontaktuppgifter',
      content: `Om du har frågor om vår användning av cookies eller denna cookiepolicy är du välkommen att kontakta oss:

E-post: info@insolvio.se

Version 1.0 — Gällande från 2026-10-07`,
    },
  ],
};

// English cookies content — Version 1.0, effective from 2026-10-07
const cookiesContentEn = {
  title: 'Cookie Policy',
  tocTitle: 'Contents',
  toc: [
    'Introduction',
    'What are cookies?',
    'How do we use cookies?',
    'Types of cookies we use',
    'Third-party cookies',
    'Your consent and how to manage cookies',
    'Legal basis',
    'Changes to this cookie policy',
    'Contact information',
  ],
  sections: [
    {
      title: '1. Introduction',
      content: `Insolvio ("we", "us" or "our") uses cookies and similar tracking technologies on our website insolvio.se (the "Website"). This cookie policy explains what cookies are, how we use them, what types of cookies we use and how you can manage your cookie settings.

Necessary cookies are used automatically because they are required for the Website to function. For any cookies that are not strictly necessary, we obtain your consent before such cookies are placed, as described in section 6 below. The mere fact that you use the Website does not constitute consent to non-necessary cookies.`,
    },
    {
      title: '2. What are cookies?',
      content: `Cookies are small text files that are stored on your device (computer, mobile phone or tablet) when you visit a website. Cookies are widely used to make websites work properly, more efficiently and to provide website owners with information about how the website is used.

Cookies can be either session cookies (which are deleted when you close your browser) or persistent cookies (which remain on your device for a specified period of time or until you manually delete them).`,
    },
    {
      title: '3. How do we use cookies?',
      content: `We currently only use cookies that are strictly necessary for the Website to function correctly and technically. If we in the future begin to use cookies for additional purposes, such as those listed below, we will update this policy and obtain your consent before such cookies are placed:`,
      bulletList: [
        'To ensure that the Website functions correctly and technically',
        'To remember your preferences and settings (not currently used – will be added if and when such cookies start being used, with prior consent where required)',
        'To analyze how visitors use the Website and improve the user experience (not currently used)',
        'To measure and analyze traffic on the Website (not currently used)',
        'For marketing purposes (not currently used)',
      ],
    },
    {
      title: '4. Types of cookies we use',
      content: `4.1 Necessary cookies

These cookies are absolutely necessary for the Website to function properly. Without these cookies, some parts of the Website cannot function. They do not require your consent and cannot be turned off. Below is a summary of all cookies used on the Website.`,
      table: {
        headers: ['Name', 'Provider', 'Description', 'Duration', 'Type'],
        rows: [
          ['__cf_bm', '.insolvio.se (Cloudflare)', 'Supports bot management and ensures that the Website is protected against malicious traffic.', '1 hour', 'Necessary'],
          ['insolvio-language', 'insolvio.se', 'Stores the user\u2019s selected language setting on the Website.', 'Permanent', 'Necessary'],
        ],
      },
    },
    {
      title: '5. Third-party cookies',
      content: `We currently do not allow third parties to place non-necessary cookies on your device via our Website. If this changes, we will update this policy, list the relevant third-party providers in the table in section 4 and obtain your prior consent before such cookies are placed.

If third-party cookies are used in the future, we have no control over them. In that case, we recommend that you read the respective third party's cookie policy for more information.`,
    },
    {
      title: '6. Your consent and how to manage cookies',
      content: `6.1 Consent

When you visit our Website for the first time, an information banner is displayed informing you that the Website only uses strictly necessary cookies that do not require your consent. If we in the future begin to use cookies that require consent, the banner will be updated so that you can choose to accept or reject each category, with equally simple options for both choices, and so that you can withdraw your consent or change your settings at any time.

6.2 Browser settings

You can also manage cookies directly through your browser settings. Most browsers allow you to:`,
      bulletList: [
        'View which cookies are stored on your device',
        'Delete cookies, either individually or all at once',
        'Block cookies from specific websites',
        'Block third-party cookies',
        'Block all cookies',
      ],
      footer: `Please note that if you choose to block or delete cookies, it may affect the functionality of our Website and your user experience may be impaired.

Instructions for the most common browsers:
• Google Chrome
• Mozilla Firefox
• Safari
• Microsoft Edge`,
    },
    {
      title: '7. Legal basis',
      content: `We process personal data collected via cookies in accordance with Regulation (EU) 2016/679 of the European Parliament and of the Council (GDPR) and the Swedish Electronic Communications Act (2003:389) (LEK), which implements the EU e-Privacy Directive (2002/58/EC).

The legal basis for:`,
      bulletList: [
        'Necessary cookies: Legitimate interest (Article 6.1 f GDPR) and technical necessity',
      ],
    },
    {
      title: '8. Changes to this cookie policy',
      content: `We reserve the right to update this cookie policy at any time. Any changes will take effect when the updated policy is published on the Website. We recommend that you regularly review this policy to stay informed about how we use cookies.`,
    },
    {
      title: '9. Contact information',
      content: `If you have questions about our use of cookies or this cookie policy, please contact us:

E-mail: info@insolvio.se

Version 1.0 — Effective from 2026-10-07`,
    },
  ],
};

export default function CookiesPage() {
  const { t, language } = useLanguage();
  const content = language === 'sv' ? cookiesContentSv : cookiesContentEn;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fdfdfd' }} data-testid="cookies-page">
      {/* Header */}
      <header style={{ borderBottom: '1px solid #e5e5e5', backgroundColor: '#fdfdfd' }}>
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="cookies-logo">
              <img src={LOGO_URL} alt="Insolvio" className="h-8 md:h-10" style={{ filter: 'invert(1)' }} />
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Link
                to="/"
                className="flex items-center gap-2 transition-colors text-sm"
                style={{ color: '#6b6b6b' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#1c8a56'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b6b6b'}
                data-testid="cookies-back-link"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('cookies.backToHome')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1
          className="text-3xl md:text-4xl font-semibold mb-8"
          style={{ fontFamily: 'Outfit, sans-serif', color: '#262626', letterSpacing: '-0.015em' }}
          data-testid="cookies-title"
        >
          {content.title}
        </h1>

        <div className="max-w-none">
          {/* Table of Contents */}
          <div className="rounded-lg p-6 mb-8" style={{ backgroundColor: '#ffffff', border: '1px solid #e5e5e5' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: '#262626', letterSpacing: '-0.015em' }}>
              {content.tocTitle}
            </h2>
            <ol className="list-decimal list-inside space-y-1" style={{ color: '#4a4a4a' }}>
              {content.toc.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ol>
          </div>

          {/* Sections */}
          {content.sections.map((section, index) => (
            <section key={index} className="mb-8">
              <h2
                className="text-xl font-semibold mb-4"
                style={{ fontFamily: 'Outfit, sans-serif', color: '#262626', letterSpacing: '-0.015em' }}
              >
                {section.title}
              </h2>
              <div className="whitespace-pre-line leading-relaxed" style={{ color: '#4a4a4a' }}>
                {section.content}
              </div>

              {/* Table if exists */}
              {section.table && (
                <div className="overflow-x-auto mt-4">
                  <table className="min-w-full text-sm" style={{ border: '1px solid #e5e5e5' }}>
                    <thead style={{ backgroundColor: '#f5f5f5' }}>
                      <tr>
                        {section.table.headers.map((header, i) => (
                          <th key={i} className="px-4 py-2 text-left font-semibold" style={{ border: '1px solid #e5e5e5', color: '#262626' }}>
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="px-4 py-2" style={{ border: '1px solid #e5e5e5', color: '#4a4a4a' }}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Bullet list if exists */}
              {section.bulletList && (
                <ul className="mt-4 list-disc list-inside space-y-1" style={{ color: '#4a4a4a' }}>
                  {section.bulletList.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}

              {/* Footer if exists */}
              {section.footer && (
                <div className="mt-4 whitespace-pre-line leading-relaxed" style={{ color: '#4a4a4a' }}>
                  {section.footer}
                </div>
              )}
            </section>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-auto" style={{ borderTop: '1px solid #e5e5e5' }}>
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm text-center" style={{ color: '#6b6b6b' }}>
            {t('footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
}
